import { World, Vec2, Circle, Box, RevoluteJoint, type Body } from 'planck';
import { TICK_RATE, RESCUE_LIMITS, inside, lineLength, emptyRecording } from './types';
import { strokeProblem, motionRect, pulseActive } from './geometry';
import type { RescueLevel, RescueRole, Recording, RescueStroke, SimulationFrame, SimulationResult, Vec, StrokeFrame } from './types';

const SCALE = 100;
const vec = (p: Vec) => Vec2(p.x / SCALE, p.y / SCALE);
type BodyTag = { role?: RescueRole; material?: string; ball?: string; stroke?: string };

/** 输入校验发生在同一份场景上：线长、区域、删除归属与事件时间都由服务端复查。 */
export function validateRecording(level: RescueLevel, value: unknown, role: RescueRole): Recording {
  if (!value || typeof value !== 'object') throw new Error('录制内容为空。');
  const raw = value as Recording;
  if (raw.version !== 1 || !Array.isArray(raw.events) || raw.events.length > RESCUE_LIMITS.events) throw new Error('录制版本或操作数量有误。');
  const result = emptyRecording(); const seen = new Set<string>(); const active = new Set<string>();
  let last = -1; let length = 0; let points = 0; let strokes = 0;
  for (const e of raw.events) {
    if (!e || !Number.isInteger(e.tick) || e.tick < last || e.tick < 0 || e.tick > level.ticks) throw new Error('操作时间顺序有误。');
    last = e.tick;
    if (e.kind === 'add') {
      const s = e.stroke;
      if (!s || typeof s.id !== 'string' || !/^[a-zA-Z0-9_-]{1,64}$/.test(s.id) || seen.has(s.id)) throw new Error('笔迹标识重复或无效。');
      if (!level.materials.includes(s.material) || !Array.isArray(s.points) || s.points.length < 2 || s.points.length > RESCUE_LIMITS.pointsPerStroke) throw new Error('笔迹材料或点数有误。');
      if (++strokes > RESCUE_LIMITS.strokes || (points += s.points.length) > RESCUE_LIMITS.points) throw new Error('笔迹过多，请简化这段操作。');
      const cleaned = s.points.map(p => {
        if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) throw new Error('笔迹坐标有误。');
        const point = { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 };
        if (!level.areas[role].some(area => inside(point, area))) throw new Error('请在自己负责的区域内画线。');
        return point;
      });
      // 每一段都须落在同一个许可矩形内，防止以两个合法端点横跨禁画区。
      for (let i = 1; i < cleaned.length; i++) if (!level.areas[role].some(r => inside(cleaned[i - 1], r) && inside(cleaned[i], r))) throw new Error('这条线跨过了禁画区域。');
      const problem = strokeProblem(level, cleaned, role, e.tick); if (problem) throw new Error(problem);
      const size = lineLength(cleaned);
      if (size < 6 || cleaned.some((p, i) => i > 0 && Math.hypot(p.x - cleaned[i - 1].x, p.y - cleaned[i - 1].y) < 0.2)) throw new Error('线条太短，请重新画一笔。');
      length += size;
      if (length > level.ink[role] + 0.1) throw new Error('墨水用完了，请撤销或重新规划。');
      seen.add(s.id); active.add(s.id);
      result.events.push({ tick: e.tick, kind: 'add', stroke: { id: s.id, material: s.material, points: cleaned } });
    } else if (e.kind === 'erase') {
      if (!active.has(e.strokeId)) throw new Error('只能擦除自己已画出的整条笔迹。');
      active.delete(e.strokeId); result.events.push({ tick: e.tick, kind: 'erase', strokeId: e.strokeId });
    } else throw new Error('未知的救援操作。');
  }
  return result;
}

/** 无 DOM、无网络、固定时间步。渲染帧率不参与模拟；同一事件顺序在三端共享。 */
export class RescueSimulation {
  readonly world = new World(Vec2(0, 10));
  tick = 0;
  private scheduled: { role: RescueRole; event: Recording['events'][number] }[] = [];
  private cursor = 0;
  private balls = new Map<string, Body>();
  private strokes = new Map<string, { body: Body; stroke: StrokeFrame }>();
  private gates = new Map<string, Body>();
  private levers = new Map<string, Body>();
  private motions = new Map<string, Body>();
  private switchAt = new Map<string, number>();
  private leverTriggered = new Set<string>();
  private checkpointReached = new Set<number>();
  private rescued = new Set<string>();
  private broken = new Set<string>();
  private touchingSpring = new Set<string>();
  private contacts = new Set<string>();
  private ink = { lead: 0, follow: 0 };
  private contributed = { lead: false, follow: false };
  private springHits = 0;
  private erased = false;
  private everLead = false;
  private won = false;
  constructor(readonly level: RescueLevel, lead: Recording = emptyRecording(), follow: Recording = emptyRecording()) {
    for (const role of ['lead', 'follow'] as const) {
      const recording = role === 'lead' ? lead : follow;
      for (const event of recording.events) {
        this.scheduled.push({ role, event });
        if (event.kind === 'add') this.ink[role] += lineLength(event.stroke.points);
      }
    }
    this.scheduled.sort((a, b) => a.event.tick - b.event.tick || (a.role === b.role ? 0 : a.role === 'lead' ? -1 : 1));
    for (const rail of level.rails) this.rail(rail.points, { material: rail.material ?? 'support' });
    for (const spec of level.balls) {
      const body = this.world.createDynamicBody({ position: vec(spec.position), bullet: true, linearDamping: 0.04, angularDamping: 0.03, userData: { ball: spec.id } });
      body.createFixture(Circle(spec.radius / SCALE), { density: spec.density ?? 1, friction: 0.2, restitution: 0 });
      if (spec.velocity) body.setLinearVelocity(vec(spec.velocity));
      this.balls.set(spec.id, body);
    }
    for (const gate of level.gates) {
      const body = this.world.createBody({ position: Vec2((gate.x + gate.w / 2) / SCALE, (gate.y + gate.h / 2) / SCALE) });
      body.createFixture(Box(gate.w / SCALE / 2, gate.h / SCALE / 2));
      this.gates.set(gate.id, body);
    }
    for (const lever of level.levers) {
      const anchor = this.world.createBody({ position: vec(lever.center) });
      const body = this.world.createDynamicBody({ position: vec(lever.center), angle: lever.angle ?? 0, angularDamping: 0.8 });
      body.createFixture(Box(lever.width / SCALE / 2, 0.045), { density: 0.3, friction: 0.25 });
      this.world.createJoint(new RevoluteJoint({ enableLimit: true, lowerAngle: -lever.limit, upperAngle: lever.limit }, anchor, body, vec(lever.center)));
      this.levers.set(lever.id, body);
    }
    for (const spec of level.motions ?? []) {
      const r = motionRect(spec, 0);
      const body = this.world.createKinematicBody({ position: Vec2((r.x + r.w / 2) / SCALE, (r.y + r.h / 2) / SCALE) });
      body.createFixture(Box(r.w / SCALE / 2, r.h / SCALE / 2), { friction: 0.65 });
      this.motions.set(spec.id, body);
    }
    this.world.on('begin-contact', contact => {
      const a = contact.getFixtureA().getBody().getUserData() as BodyTag | undefined;
      const b = contact.getFixtureB().getBody().getUserData() as BodyTag | undefined;
      for (const [ball, rail] of [[a, b], [b, a]]) if (ball?.ball && rail?.role) {
        this.contributed[rail.role] = true; this.contacts.add(ball.ball + ':' + rail.role);
        if (rail.material === 'spring') {
          const key = ball.ball + ':' + rail.stroke;
          if (!this.touchingSpring.has(key)) { this.springHits++; this.touchingSpring.add(key); }
        }
      }
    });
  }
  private rail(points: Vec[], tag: BodyTag): Body {
    const body = this.world.createBody({ userData: tag });
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]; const b = points[i]; const length = Math.hypot(b.x - a.x, b.y - a.y);
      if (length < 0.2) continue;
      body.createFixture(Box(length / SCALE / 2, 0.025, Vec2((a.x + b.x) / SCALE / 2, (a.y + b.y) / SCALE / 2), Math.atan2(b.y - a.y, b.x - a.x)), { friction: 0.12, restitution: tag.material === 'spring' ? 0.85 : 0 });
    }
    return body;
  }
  private apply() {
    while (this.cursor < this.scheduled.length && this.scheduled[this.cursor].event.tick <= this.tick) {
      const { role, event } = this.scheduled[this.cursor++]; const key = role + ':' + (event.kind === 'add' ? event.stroke.id : event.strokeId);
      if (event.kind === 'add') {
        const body = this.rail(event.stroke.points, { role, material: event.stroke.material, stroke: key });
        this.strokes.set(key, { body, stroke: { ...event.stroke, role } });
      } else {
        const found = this.strokes.get(key);
        if (found) { this.world.destroyBody(found.body); this.strokes.delete(key); this.erased = true; }
      }
    }
  }
  step() {
    if (this.tick >= this.level.ticks) return;
    this.apply();
    for (const spec of this.level.motions ?? []) {
      const a = motionRect(spec, this.tick), b = motionRect(spec, this.tick + 1);
      const body = this.motions.get(spec.id)!;
      body.setTransform(Vec2((a.x + a.w / 2) / SCALE, (a.y + a.h / 2) / SCALE), 0);
      body.setLinearVelocity(Vec2((b.x - a.x) * TICK_RATE / SCALE, (b.y - a.y) * TICK_RATE / SCALE));
    }
    for (const wind of this.level.winds ?? []) if (pulseActive(wind,this.tick)) for (const body of this.balls.values()) {
      const p = body.getPosition();
      if (body.isActive() && inside({x:p.x*SCALE,y:p.y*SCALE},wind)) body.applyForceToCenter(Vec2(wind.force.x / SCALE * body.getMass(), wind.force.y / SCALE * body.getMass()), true);
    }
    for (const gate of this.level.gates) {
      const tests = gate.switches.map(id => this.leverTriggered.has(id) || (this.switchAt.has(id) && this.tick >= this.switchAt.get(id)! + (gate.delay ?? 0) && this.tick < this.switchAt.get(id)! + (gate.delay ?? 0) + (this.level.switches.find(s => s.id === id)?.hold ?? this.level.ticks)));
      const open = gate.all ? tests.every(Boolean) : tests.some(Boolean);
      this.gates.get(gate.id)!.setActive(!open);
    }
    const velocities = new Map([...this.balls].map(([id, body]) => [id, { ...body.getLinearVelocity() }]));
    this.world.step(1 / TICK_RATE, 8, 3); this.tick++;
    for (const lever of this.level.levers) if (this.levers.get(lever.id)!.getAngle() * lever.direction >= lever.threshold) this.leverTriggered.add(lever.id);
    for (const spec of this.level.balls) {
      const body = this.balls.get(spec.id)!; const p = body.getPosition(); const position = { x: p.x * SCALE, y: p.y * SCALE };
      if (this.rescued.has(spec.id)) continue;
      const before = velocities.get(spec.id)!; const after = body.getLinearVelocity();
      if (spec.fragile && Math.hypot(after.x - before.x, after.y - before.y) > spec.fragile) this.broken.add(spec.id);
      if (!spec.helper && (position.y > 1080 || position.x < -80 || position.x > 1080 || this.level.hazards.some(r => inside(position, r)))) this.broken.add(spec.id);
      if (!spec.helper && this.level.pulses?.some(r => pulseActive(r,this.tick) && inside(position,r,spec.radius))) this.broken.add(spec.id);
      for (const s of this.level.switches) if ((!s.ball || s.ball === spec.id) && inside(position, s) && !this.switchAt.has(s.id)) this.switchAt.set(s.id, this.tick);
      this.level.checkpoints.forEach((r, i) => { if (r.ball === spec.id && inside(position, r) && this.contacts.has(spec.id + ':lead')) this.checkpointReached.add(i); });
      for (const goal of this.level.goals) if (goal.ball === spec.id && inside(position, goal) && !this.broken.has(spec.id)) {
        this.rescued.add(spec.id); body.setLinearVelocity(Vec2()); body.setAngularVelocity(0); body.setActive(false);
      }
    }
    const mechanisms = this.level.requiredSwitches.every(id => this.switchAt.has(id)) && this.level.requiredLevers.every(id => this.leverTriggered.has(id));
    if (this.contributed.lead && this.checkpointReached.size === this.level.checkpoints.length && mechanisms) this.everLead = true;
    const simultaneous = !this.level.simultaneous || this.level.requiredSwitches.every(id => this.tick - (this.switchAt.get(id) ?? -10000) < (this.level.switches.find(s => s.id === id)?.hold ?? 0));
    // 第 9 关的旧快照有隐藏的“双弹板”计数；到家后物体已冻结，任何合法救援路线都应成功。
    const springRequirement = this.level.version >= 2 || this.level.id === 'rescue-009' ? 0 : this.level.requiredSprings;
    if (this.everLead && this.contributed.follow && this.level.goals.every(g => this.rescued.has(g.ball)) && mechanisms && simultaneous && this.springHits >= springRequirement && (!this.level.requiredErase || this.erased)) this.won = true;
  }
  advance(tick: number) { while (this.tick < Math.min(tick, this.level.ticks)) this.step(); this.apply(); return this.frame(); }
  frame(): SimulationFrame {
    const failed = this.broken.size > 0;
    const objectives = [
      { label: '先手交接', complete: this.everLead },
      { label: '后手接住', complete: this.contributed.follow },
      { label: '全部到家', complete: this.level.goals.every(g => this.rescued.has(g.ball)) },
      ...(this.level.requiredErase ? [{ label: '撤去临时桥', complete: this.erased }] : []),
      ...(this.level.version < 2 && this.level.id !== 'rescue-009' && this.level.requiredSprings ? [{label: '弹板 ' + this.springHits + '/' + this.level.requiredSprings,complete:this.springHits>=this.level.requiredSprings}] : []),
      ...(this.level.simultaneous ? [{label:'同时触发开关',complete:this.level.requiredSwitches.every(id => this.tick - (this.switchAt.get(id) ?? -10000) < (this.level.switches.find(s=>s.id===id)?.hold ?? 0))}] : []),
    ];
    const missing = objectives.filter(o=>!o.complete).map(o=>o.label);
    return {
      tick: this.tick, balls: this.level.balls.map(s => { const p = this.balls.get(s.id)!.getPosition(); return { id: s.id, x: p.x * SCALE, y: p.y * SCALE, radius: s.radius, color: s.color, broken: this.broken.has(s.id), rescued: this.rescued.has(s.id), helper: !!s.helper, fragile: !!s.fragile }; }),
      strokes: [...this.strokes.values()].map(s => s.stroke),
      gates: [...this.gates].map(([id, b]) => ({ id, open: !b.isActive() })),
      levers: [...this.levers].map(([id, b]) => ({ id, angle: b.getAngle() })),
      motions: (this.level.motions ?? []).map(m => ({ id: m.id, ...motionRect(m,this.tick) })),
      pulses: (this.level.pulses ?? []).map(p => ({id:p.id,active:pulseActive(p,this.tick)})), objectives,
      switches: [...this.switchAt.keys()], leadPassed: this.everLead, success: this.won && !failed, failed,
      message: failed ? '小伙伴没有安全到达，回退一点再试。' : this.won ? '你们一起接住了这一程！' : '还差：' + missing.join(' · ') + (this.tick >= this.level.ticks ? '；回退或重画再试。' : '。'),
      contributions: { ...this.contributed }, springHits: this.springHits, erased: this.erased,
    };
  }
  result(): SimulationResult { return { ...this.frame(), ink: { ...this.ink } }; }
}
export function simulate(level: RescueLevel, lead: Recording, follow: Recording = emptyRecording()): SimulationResult {
  const simulation = new RescueSimulation(level, lead, follow);
  simulation.advance(level.ticks); return simulation.result();
}
